// src/pages/dashboard/branch/menu-items/components/tabs/AdditionalDetailsTab.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiClock, FiActivity, FiPackage } from 'react-icons/fi';
import InputField from '../../../../../../../components/ui/InputField';
import ItemIngredientsManager from '../../../../../../../components/ui/ItemIngredientsManager';
import { FormData } from '../AddMenuItemModal';
import { ItemIngredientsState } from '../../../../../../../types/ingredient';

interface AdditionalDetailsTabProps {
	formData: FormData;
	ingredients: string[];
	arIngredients: string[];
	onFormDataChange: (data: FormData) => void;
	onIngredientsChange: (ingredients: string[]) => void;
	onArIngredientsChange: (ingredients: string[]) => void;
	branchId: string;
	itemIngredients?: ItemIngredientsState;
	onItemIngredientsChange?: (ingredients: ItemIngredientsState) => void;
}

const AdditionalDetailsTab: React.FC<AdditionalDetailsTabProps> = ({
	formData,
	ingredients,
	arIngredients,
	onFormDataChange,
	branchId,
	itemIngredients = { individual_ingredients: [], ingredient_groups: [] },
	onItemIngredientsChange,
}) => {
	const { t } = useTranslation();

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		onFormDataChange({
			...formData,
			[name]: value
		});
	};

	const handleItemIngredientsChange = (newIngredients: ItemIngredientsState) => {
		if (onItemIngredientsChange) {
			onItemIngredientsChange(newIngredients);
		}
	};

	return (
		<div className="space-y-6">
			{/* Nutrition and Preparation */}
			<div className="space-y-4">
				<div className="flex items-center gap-2 mb-4">
					<FiActivity className="w-5 h-5 text-primary" />
					<h4 className="text-md font-medium text-gray-900 dark:text-white">
						{t('menuItems.nutritionAndPreparation')}
					</h4>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<InputField
						label={t('menuItems.calories')}
						name="calories"
						type="number"
						min="0"
						value={formData.calories}
						onChange={handleInputChange}
						placeholder="250"
						icon={<FiActivity />}
					/>

					<InputField
						label={t('menuItems.preparationTime')}
						name="preparationTime"
						type="number"
						min="0"
						value={formData.preparationTime}
						onChange={handleInputChange}
						placeholder="15"
						icon={<FiClock />}
					/>

					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							{t('menuItems.spiceLevel')}
						</label>
						<select
							name="spiceLevel"
							value={formData.spiceLevel}
							onChange={handleInputChange}
							className="w-full h-[49.6px] px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
						>
							<option value="none">{t('menuItems.spice.none')}</option>
							<option value="mild">{t('menuItems.spice.mild')}</option>
							<option value="medium">{t('menuItems.spice.medium')}</option>
							<option value="hot">{t('menuItems.spice.hot')}</option>
							<option value="extra_hot">{t('menuItems.spice.extra_hot')}</option>
						</select>
					</div>
				</div>

				{/* Additional Info */}
				<div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
					<h5 className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-2">
						{t('menuItems.nutritionalInfo')}
					</h5>
					<p className="text-xs text-blue-600 dark:text-blue-300">
						{t('menuItems.nutritionalInfoDescription')}
					</p>
				</div>
			</div>

			{/* Ingredient Management */}
			<div className="space-y-4">
				<div className="flex items-center gap-2 mb-4">
					<FiPackage className="w-5 h-5 text-primary" />
					<h4 className="text-md font-medium text-gray-900 dark:text-white">
						Ingredient Management
					</h4>
				</div>

				<div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
					<h5 className="text-sm font-medium text-green-800 dark:text-green-400 mb-2">
						Enhanced Ingredient System
					</h5>
					<p className="text-xs text-green-600 dark:text-green-300">
						Add individual ingredients or entire ingredient groups to this menu item. Create ingredients and groups in Settings → Ingredients first.
					</p>
				</div>

				<ItemIngredientsManager
					branchId={branchId}
					value={itemIngredients}
					onChange={handleItemIngredientsChange}
				/>
			</div>
		</div>
	);
};

export default AdditionalDetailsTab;